import { ElMessage } from 'element-plus';
import { request } from '@/utils/request';
import asrService from '@/services/modules/asrService'; // 导入ASR服务

// 语音服务配置
interface VoiceConfig {
  language: string;
  rate: number;
  pitch: number;
  volume: number;
}

// 语音服务状态
interface VoiceState {
  isSpeaking: boolean;
  currentUtteranceId: string | null;
  speechSynthesis: SpeechSynthesis | null;
}

// 缓存接口
interface VoiceCacheItem {
  audioUrl: string;
  text: string;
  timestamp: number;
}

class VoiceService {
  private config: VoiceConfig = {
    language: 'zh-CN',
    rate: 0.8,
    pitch: 1.0,
    volume: 0.9
  };
  
  private state: VoiceState = {
    isSpeaking: false,
    currentUtteranceId: null,
    speechSynthesis: typeof window !== 'undefined' ? window.speechSynthesis : null
  };
  
  private cache = new Map<string, VoiceCacheItem>();
  private CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7天
  
  // 检查浏览器是否支持 Web Speech API
  public isWebSpeechSupported(): boolean {
    return typeof window !== 'undefined' && 
           'speechSynthesis' in window && 
           this.state.speechSynthesis !== null;
  }
  
  // 获取可用的语音列表
  public getAvailableVoices(): SpeechSynthesisVoice[] {
    if (!this.state.speechSynthesis) return [];
    return this.state.speechSynthesis.getVoices();
  }
  
  // 检查是否有可用的语音
  public hasAvailableVoices(): boolean {
    const voices = this.getAvailableVoices();
    return voices.length > 0;
  }
  
  // 检测文本语言
  private detectLanguage(text: string): string {
    // 简单的语言检测：如果包含中文字符，使用中文
    const chineseRegex = /[\u4e00-\u9fff]/;
    if (chineseRegex.test(text)) {
      return 'zh-CN';
    }
    // 如果包含英文字符，使用英文
    const englishRegex = /[a-zA-Z]/;
    if (englishRegex.test(text)) {
      return 'en-US';
    }
    return 'zh-CN'; // 默认中文
  }
  
  // 获取合适的语音
  private getBestVoice(language: string) {
    if (!this.state.speechSynthesis) return null;
    
    const voices = this.getAvailableVoices();
    if (voices.length === 0) return null;
    
    // 优先选择指定语言的语音
    const targetVoices = voices.filter(voice => 
      voice.lang.startsWith(language) || 
      voice.name.toLowerCase().includes(language.toLowerCase())
    );
    
    if (targetVoices.length > 0) {
      // 优先选择女性语音（通常更适合儿童）
      const femaleVoices = targetVoices.filter(voice => 
        voice.name.toLowerCase().includes('female') || 
        voice.name.toLowerCase().includes('woman') ||
        voice.name.toLowerCase().includes('girl') ||
        voice.name.toLowerCase().includes('fem')
      );
      return femaleVoices.length > 0 ? femaleVoices[0] : targetVoices[0];
    }
    
    // 如果没有匹配的语言，返回第一个可用的语音
    return voices[0] || null;
  }
  
  // 使用 Web Speech API 播放语音
  public async speakWithWebSpeech(text: string, onEndCallback?: () => void): Promise<void> {
    if (!this.isWebSpeechSupported()) {
      throw new Error('当前浏览器不支持语音朗读功能');
    }
    
    // 取消当前正在播放的语音
    this.cancel();
    
    const language = this.detectLanguage(text);
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = language === 'en-US' ? 0.8 : 0.8;
    utterance.pitch = this.config.pitch;
    utterance.volume = this.config.volume;
    
    const bestVoice = this.getBestVoice(language);
    if (bestVoice) {
      utterance.voice = bestVoice;
    } else {
      console.warn('未找到合适的语音，使用默认语音');
    }
    
    // 设置唯一ID用于跟踪
    const utteranceId = `utterance-${Date.now()}-${Math.random()}`;
    utterance.addEventListener('start', () => {
      this.state.isSpeaking = true;
      this.state.currentUtteranceId = utteranceId;
    });
    
    utterance.addEventListener('end', () => {
      this.state.isSpeaking = false;
      this.state.currentUtteranceId = null;
      if (onEndCallback) {
        onEndCallback();
      }
    });
    
    utterance.addEventListener('error', (event) => {
      console.error('语音播放错误:', event);
      this.state.isSpeaking = false;
      this.state.currentUtteranceId = null;
      
      // 如果是 synthesis-failed 错误，尝试降级到 CosyVoice
      if (event.error === 'synthesis-failed' || event.error === 'not-allowed') {
        ElMessage.warning('本地语音服务不可用，尝试使用高质量语音服务...');
        // 这里不能直接调用 CosyVoice，因为可能会造成循环调用
        // 应该在上层处理降级逻辑
        throw new Error('Web Speech API 不可用，请启用高质量语音服务');
      } else {
        ElMessage.error('语音播放失败，请稍后重试');
      }
    });
    
    // 等待语音合成器加载完成
    if (this.getAvailableVoices().length === 0) {
      return new Promise((resolve, reject) => {
        const voicesChangedHandler = () => {
          window.speechSynthesis.removeEventListener('voiceschanged', voicesChangedHandler);
          try {
            this.state.speechSynthesis!.speak(utterance);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        window.speechSynthesis.addEventListener('voiceschanged', voicesChangedHandler);
        
        // 设置超时，避免无限等待
        setTimeout(() => {
          window.speechSynthesis.removeEventListener('voiceschanged', voicesChangedHandler);
          reject(new Error('语音合成器加载超时'));
        }, 3000);
      });
    } else {
      this.state.speechSynthesis!.speak(utterance);
    }
  }
  
  // 使用阿里云 CosyVoice 播放语音
  public async speakWithCosyVoice(text: string, onEndCallback?: () => void): Promise<void> {
    try {
      // 检查缓存
      const cacheKey = this.generateCacheKey(text);
      const cachedItem = this.cache.get(cacheKey);
      
      if (cachedItem && Date.now() - cachedItem.timestamp < this.CACHE_EXPIRY) {
        // 使用缓存的音频
        await this.playAudioFromUrl(cachedItem.audioUrl, onEndCallback);
        return;
      }
      
      // 调用 CosyVoice API
      console.log('正在调用 CosyVoice API...');
      const response = await request.post('/api/cosyvoice/synthesize', {
        text: text,
        model: 'cosyvoice-v3-flash',
        mode: 'instruct'
      });
      console.log('CosyVoice 语音合成结果:', response);
      
      if (response && response.data && response.data.audioUrl) {
        const audioUrl = response.data.audioUrl;
        // 缓存结果
        this.cache.set(cacheKey, {
          audioUrl,
          text,
          timestamp: Date.now()
        });
        await this.playAudioFromUrl(audioUrl, onEndCallback);
      } else {
        throw new Error('语音合成失败：未返回有效的音频URL');
      }
    } catch (error: any) {
      console.error('CosyVoice 语音合成错误:', error);
      if (error.message === '无响应，请检查网络') {
        ElMessage.error('网络连接失败，请检查后端服务是否正常运行');
      } else if (error.message === '服务暂时不可用，请稍后再试') {
        ElMessage.error('语音合成服务暂时不可用');
      } else {
        ElMessage.error('高质量语音服务暂时不可用');
      }
      throw error; // 重新抛出错误，让上层处理
    }
  }
  
  // 使用阿里云 CosyVoice 流式播放语音
  public async streamSpeakWithCosyVoice(
    textStream: string, 
    onAudioChunk?: (audioData: string) => void,
    onComplete?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    try {
      const url = '/api/cosyvoice/stream';
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          text: textStream,
          model: 'cosyvoice-v3-flash',
          voice: 'longanyang',
          speed: 0.8,
          volume: 1.0,
          pitch: 1.0
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No reader available');
      }
      
      let buffer = '';
      let currentEvent = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // 处理 SSE 格式的数据
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          
          if (trimmedLine.startsWith('event:')) {
            currentEvent = trimmedLine.substring(6).trim();
          } else if (trimmedLine.startsWith('data:')) {
            try {
              const dataStr = trimmedLine.substring(5).trim();
              console.log('语音SSE数据:', dataStr);
              if (currentEvent === 'voice_data') {
                // 接收到音频数据块
                if (onAudioChunk) {
                  onAudioChunk(dataStr);
                }
              } else if (currentEvent === 'voice_complete') {
                // 语音合成完成
                if (onComplete) {
                  onComplete();
                }
                break;
              } else if (currentEvent === 'voice_error') {
                // 语音合成错误
                if (onError) {
                  onError(dataStr || '语音合成失败');
                }
                break;
              } else if (currentEvent === 'voice_connected') {
                // 连接成功
                console.log('语音合成连接成功');
              }
            } catch (e) {
              console.error('解析语音SSE数据失败:', e);
              if (onError) {
                onError('语音数据解析错误');
              }
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('流式语音合成请求失败:', error);
      if (onError) {
        onError('网络连接失败，请检查网络后重试');
      }
    }
  }
  
  // 使用阿里云 CosyVoice 双向流式播放语音
  public async bidirectionalStreamSpeakWithCosyVoice(
    text: string, 
    onAudioComplete?: (completeAudioData: string) => void,
    onComplete?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    console.log('【双向流式语音合成】开始处理文本:', text.substring(0, 50) + '...');
    
    try {
      const url = '/api/cosyvoice/stream/bidirectional';
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          text: text,
          model: 'cosyvoice-v3-flash',
          voice: 'longanyang',
          speed: 0.8,
          volume: 1.0,
          pitch: 1.0
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      console.log('【双向流式语音合成】连接成功，开始接收音频数据...');
      
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) {
        throw new Error('No reader available');
      }
      
      let buffer = '';
      let currentEvent = '';
      let audioChunks: string[] = [];
      let audioChunkCount = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('【双向流式语音合成】数据接收完成，共收到', audioChunkCount, '个音频块');
          break;
        }
        
        buffer += decoder.decode(value, { stream: true });
        
        // 处理 SSE 格式的数据
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          
          if (trimmedLine.startsWith('event:')) {
            currentEvent = trimmedLine.substring(6).trim();
          } else if (trimmedLine.startsWith('data:')) {
            try {
              const dataStr = trimmedLine.substring(5).trim();
              
              if (currentEvent === 'voice_data') {
                // 收集音频数据块，不立即播放
                audioChunkCount++;
                console.log('【双向流式语音合成】收到第', audioChunkCount, '个音频块，长度:', dataStr.length);
                audioChunks.push(dataStr);
              } else if (currentEvent === 'voice_complete') {
                // 语音合成完成，合并所有音频块并回调
                console.log('【双向流式语音合成】语音合成完成事件');
                if (audioChunks.length > 0) {
                  const completeAudioData = audioChunks.join('');
                  if (onAudioComplete) {
                    onAudioComplete(completeAudioData);
                  }
                }
                if (onComplete) {
                  onComplete();
                }
                break;
              } else if (currentEvent === 'voice_error') {
                // 语音合成错误
                console.error('【双向流式语音合成】收到错误事件:', dataStr);
                if (onError) {
                  onError(dataStr || '语音合成失败');
                }
                break;
              } else if (currentEvent === 'voice_connected') {
                // 连接成功
                console.log('【双向流式语音合成】语音合成连接成功');
              }
            } catch (e) {
              console.error('【双向流式语音合成】解析语音SSE数据失败:', e);
              if (onError) {
                onError('语音数据解析错误');
              }
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('【双向流式语音合成】请求失败:', error);
      if (onError) {
        onError('网络连接失败，请检查网络后重试');
      }
    }
  }
  
  // 从URL播放音频
  private async playAudioFromUrl(url: string, onEndCallback?: () => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(url);
      audio.volume = this.config.volume;
      
      audio.onloadeddata = () => {
        this.state.isSpeaking = true;
      };
      
      audio.onended = () => {
        this.state.isSpeaking = false;
        if (onEndCallback) {
          onEndCallback();
        }
        resolve();
      };
      
      audio.onerror = (error) => {
        this.state.isSpeaking = false;
        console.error('音频播放错误:', error);
        reject(error);
      };
      
      audio.play().catch(reject);
    });
  }
  
  // 生成缓存键
  private generateCacheKey(text: string): string {
    return `voice_${text.substring(0, 100)}`;
  }
  
  // 播放语音
  public async speak(text: string, onEndCallback?: () => void): Promise<void> {
    if (!text || !text.trim()) {
      console.warn('Empty text provided to speak');
      return;
    }
    
    // B方案：通知ASR服务TTS开始
    if (asrService) {
      asrService.onTtsStart();
    }
    
    // 使用 Web Speech API
    if (this.isWebSpeechSupported() && this.hasAvailableVoices()) {
      await this.speakWithWebSpeech(text, onEndCallback);
    } else {
      // 使用 CosyVoice API
      await this.speakWithCosyVoice(text, onEndCallback);
    }
  }
  
  // 取消当前语音播放
  public cancel(): void {
    if (this.state.speechSynthesis) {
      this.state.speechSynthesis.cancel();
    }
    
    // B方案：通知ASR服务TTS结束
    if (asrService) {
      asrService.onTtsEnd();
    }
    
    this.state.isSpeaking = false;
    this.state.currentUtteranceId = null;
  }
  
  // 暂停语音
  public pause(): void {
    if (this.state.speechSynthesis) {
      this.state.speechSynthesis.pause();
    }
    this.state.isSpeaking = false;
  }
  
  // 恢复语音
  public resume(): void {
    if (this.state.speechSynthesis) {
      this.state.speechSynthesis.resume();
    }
    this.state.isSpeaking = true;
  }
  
  // 检查是否正在说话
  public isSpeaking(): boolean {
    return this.state.isSpeaking;
  }
  
  // 更新配置
  public updateConfig(newConfig: Partial<VoiceConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
  
  // 清理缓存
  public clearCache(): void {
    this.cache.clear();
    ElMessage.success('语音缓存已清理');
  }
  
  // 获取缓存统计
  public getCacheStats(): { size: number; items: number } {
    let totalSize = 0;
    this.cache.forEach(item => {
      totalSize += item.audioUrl.length + item.text.length;
    });
    return {
      size: totalSize,
      items: this.cache.size
    };
  }
}

// 创建全局语音服务实例
const voiceService = new VoiceService();

export default voiceService;
export type { VoiceConfig, VoiceState };